import { StandardEvents } from '@shopify/events';
import { ThemeEvents } from '@theme/events';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import { getScrollTop, scrollTo as scrollPageTo } from '@theme/scroll-container';
import '@theme/product-form';

/** Pure shared resolver. Quantity readiness never establishes a free reward. */
export function resolveTinctureOffer(snapshot, pending = false) {
  const quantity = Math.max(0, Number(snapshot?.quantity) || 0);
  const units = Math.max(0, Number(snapshot?.freeUnits) || 0);
  const savings = Math.max(0, Number(snapshot?.savings) || 0);
  const expected = Math.floor(quantity / 5);
  const confirmed = !pending && quantity >= 5 && savings > 0 && units > 0 && units <= expected;
  const progress = Math.min(quantity, 5);
  const nextProgress = confirmed ? quantity % 5 : 0;
  const state = pending ? 'pending' : confirmed ? 'confirmed' : quantity >= 5 ? 'not-applied' : 'shopping';
  return {
    enabled: Boolean(snapshot?.enabled), codeApplied: !pending && Boolean(snapshot?.codeApplied),
    quantity, units: confirmed ? units : 0, savings: pending ? 0 : savings,
    progress, remaining: Math.max(0, 5 - quantity), state, confirmed,
    nextProgress, nextRemaining: nextProgress ? 5 - nextProgress : 0,
    missingReward: confirmed && units < expected,
    recommend: !pending && quantity > 0 && (quantity < 5 || (confirmed && nextProgress > 0 && units === expected)),
  };
}

const roots = new Set();
const inFlight = new Set();
const runtime = document.getElementById('tincture-offer-runtime');
const config = runtime ? JSON.parse(runtime.textContent) : {};
const text = config.text || {};
let current = null;
let generation = 0;
let refreshPromise = null;
let announcement = '';
let initiatingForm = null;
let initiatingFocus = null;
let initiatingContext = null;
let initiatingDialog = null;
let hiddenAt = 0;

const phrase = (key, variables = {}) => {
  let value = text[key] || '';
  for (const [name, replacement] of Object.entries(variables)) value = value.replaceAll('__' + name + '__', String(replacement));
  return value;
};

function readSnapshot(source) {
  const element = source.querySelector('[data-tincture-cart-state]');
  if (!element) return null;
  try {
    const data = JSON.parse(element.textContent);
    if (data.version !== 1 || !Number.isInteger(data.quantity) || data.quantity < 0 ||
      !Number.isInteger(data.freeUnits) || data.freeUnits < 0 || !Number.isFinite(data.savings)) return null;
    return data;
  } catch { return null; }
}

function snapshotFromSections(sections) {
  for (const html of Object.values(sections || {})) {
    if (typeof html !== 'string') continue;
    const snapshot = readSnapshot(new DOMParser().parseFromString(html, 'text/html'));
    if (snapshot) return snapshot;
  }
  return null;
}

function update(announce = false, unknown = false) {
  const state = resolveTinctureOffer(current, inFlight.size > 0);
  if (unknown) state.state = 'unknown';
  if (current?.endsAt && Number.isFinite(Date.parse(current.endsAt)) && Date.now() >= Date.parse(current.endsAt)) state.enabled = false;
  for (const root of roots) root.render(state, unknown);
  for (const badge of document.querySelectorAll('[data-tincture-line-reward]')) {
    badge.hidden = !state.enabled || inFlight.size > 0 || unknown;
  }
  if (!announce || inFlight.size || !state.enabled) return;
  const message = getMessage(state, unknown);
  const signature = state.quantity + '|' + state.units + '|' + state.savings + '|' + state.state + '|' + state.codeApplied;
  if (signature === announcement) return;
  announcement = signature;
  const region = document.getElementById('tincture-offer-announcement');
  if (region) region.textContent = message;
}

function getMessage(state, unknown) {
  if (unknown) return phrase('unknown');
  if (state.state === 'pending') return phrase(state.quantity >= 5 ? 'checking' : 'updating', {count: state.quantity});
  const codeMessage = state.codeApplied ? ' ' + phrase('code_also_applied') : '';
  if (state.confirmed) return phrase(state.units === 1 ? 'confirmed_one' : 'confirmed', {count: state.units}) + codeMessage;
  if (state.quantity >= 5) return state.savings > 0 ? phrase('allocated') + codeMessage : phrase(state.codeApplied ? 'code_instead' : 'not_applied');
  if (state.quantity === 4) return phrase('one_away');
  if (state.quantity > 0) return phrase('add_more', {count: state.remaining});
  return phrase('empty');
}

/** One read-only recovery for all surfaces, using the existing cart sections. No POST is replayed. */
async function refresh() {
  if (refreshPromise) return refreshPromise;
  if (inFlight.size) return;
  const revision = ++generation;
  refreshPromise = (async () => {
    const controllers = [...document.querySelectorAll('cart-items-component[data-section-id]')];
    const ids = [...new Set(controllers.map(element => element.dataset.sectionId))].slice(0, 5);
    if (!ids.length) return update(false, true);
    // Render in the current page context: Horizon omits its drawer on /cart.
    const url = new URL(location.href);
    url.hash = '';
    url.searchParams.delete('section_id');
    url.searchParams.set('sections', ids.join(','));
    url.searchParams.set('_tp', String(Date.now()));
    // The collection endpoint negotiates collection JSON when Accept is application/json.
    // Use the same request headers as Horizon's section renderer.
    const response = await fetch(url, {credentials: 'same-origin', cache: 'no-store'});
    if (!response.ok) throw Error('Cart display could not be refreshed');
    const sections = await response.json();
    const snapshot = snapshotFromSections(sections);
    if (revision !== generation || inFlight.size) return;
    if (!snapshot) return update(false, true);
    current = snapshot;
    for (const id of ids) {
      if (!sections[id]) continue;
      sectionRenderer.abortRender(id);
      const controller = controllers.find(element => element.dataset.sectionId === id);
      await morphSection(id, sections[id], {mode: controller?.hasAttribute('data-drawer') ? 'hydration' : 'full', injectStylesheet: true});
    }
    update(true);
    document.querySelector('theme-drawer#cart-drawer')?.dispatchEvent(new CustomEvent(ThemeEvents.cartSectionRestored, {bubbles: true}));
  })().catch(error => {
    console.warn('[tincture-offer] Cart display refresh failed:', error);
    if (revision === generation && !inFlight.size) update(false, true);
  }).finally(() => { refreshPromise = null; });
  return refreshPromise;
}

function preserveRecommendationFocus() {
  if (!initiatingFocus) return;
  const focus = initiatingFocus;
  const context = initiatingContext;
  const dialog = initiatingDialog;
  initiatingFocus = null;
  initiatingForm = null;
  initiatingContext = null;
  initiatingDialog = null;
  requestAnimationFrame(() => {
    if (focus.isConnected && focus.checkVisibility()) return;
    if (document.activeElement !== document.body && document.activeElement !== dialog && document.activeElement?.checkVisibility()) return;
    const target = [...roots].find(root => root.dataset.context === context && !root.hidden);
    target?.querySelector('[data-tincture-status]')?.focus({preventScroll: true});
  });
}

function recommendationFeedback(form, key) {
  // Native section morphs can preserve the component while replacing its form children.
  const feedback = form?.querySelector('[data-tincture-rec-feedback]');
  if (feedback) feedback.textContent = phrase(key);
}

function handleCartEvent(event) {
  const revision = ++generation;
  const token = {};
  inFlight.add(token);
  const timeout = setTimeout(() => {
    if (!inFlight.delete(token)) return;
    generation++;
    if (!inFlight.size) refresh();
  }, 15000);
  const form = event.target instanceof Element ? event.target.closest('[data-tincture-recommendation]') : null;
  if (form) {
    initiatingForm = form;
    initiatingFocus = document.activeElement;
    initiatingContext = form.closest('tincture-offer')?.dataset.context;
    initiatingDialog = form.closest('dialog');
    recommendationFeedback(form, 'adding');
  }
  update(false);
  if (!event.promise) {
    clearTimeout(timeout);
    inFlight.delete(token);
    refresh();
    return;
  }
  Promise.resolve(event.promise).then(({detail}) => {
    clearTimeout(timeout);
    inFlight.delete(token);
    if (detail?.didError) {
      if (form?.isConnected) recommendationFeedback(form, 'add_failed');
      if (!inFlight.size) return refresh();
      return;
    }
    // A later mutation can change Shopify's line keys and reward allocation.
    if (revision !== generation) {
      if (!inFlight.size) return refresh();
      return;
    }
    const snapshot = snapshotFromSections(detail?.sections);
    if (!snapshot || inFlight.size) {
      if (!inFlight.size) return refresh();
      return;
    }
    current = snapshot;
    update(true);
    requestAnimationFrame(() => {
      update(false);
      if (form?.isConnected) recommendationFeedback(form, 'added');
      preserveRecommendationFocus();
    });
  }).catch(() => {
    clearTimeout(timeout);
    inFlight.delete(token);
    if (form?.isConnected) recommendationFeedback(form, 'add_failed');
    if (!inFlight.size) refresh();
  });
}

class TinctureOffer extends HTMLElement {
  connectedCallback() {
    roots.add(this);
    if (!current) current = readSnapshot(document);
    this.addEventListener('click', this.onClick);
    this.addEventListener('submit', this.onSubmit, true);
    update(false);
  }
  disconnectedCallback() {
    roots.delete(this);
    this.removeEventListener('click', this.onClick);
    this.removeEventListener('submit', this.onSubmit, true);
  }
  onSubmit = event => {
    if (inFlight.size && event.target.closest('[data-tincture-recommendation]')) {
      event.preventDefault();
      event.stopPropagation();
    }
  };
  onClick = async event => {
    const target = event.target.closest('a,button');
    if (!target) return;
    if (target.hasAttribute('data-tincture-retry')) return refresh();
    if (!target.hasAttribute('data-tincture-action') && !target.hasAttribute('data-tincture-browse')) return;
    const url = new URL(target.href, location.origin);
    const drawer = this.closest('theme-drawer');
    if (url.pathname === Theme.routes.cart_url && this.dataset.context !== 'cart') {
      const cartDrawer = document.querySelector('theme-drawer#cart-drawer');
      if (cartDrawer?.open) {
        event.preventDefault();
        cartDrawer.open();
      }
      return;
    }
    if (url.pathname === location.pathname && url.hash === '#ResultsList') {
      const grid = document.getElementById('ResultsList');
      if (!grid) return;
      event.preventDefault();
      // Closing a modal drawer restores its previous page scroll position.
      // Wait for that restoration before revealing the collection results.
      await drawer?.close?.();
      const header = document.getElementById('header-component');
      const offset = header?.getBoundingClientRect().height || 0;
      scrollPageTo({top: grid.getBoundingClientRect().top + getScrollTop() - offset - 20, behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth'});
      return;
    }
    drawer?.close?.();
  };
  render(state, unknown) {
    const context = this.dataset.context;
    const cart = context === 'drawer' || context === 'cart';
    this.hidden = !state.enabled || (cart && state.quantity === 0);
    this.dataset.state = unknown ? 'unknown' : state.state;
    this.setAttribute('aria-busy', String(state.state === 'pending'));
    const bar = this.querySelector('[role="progressbar"]');
    const count = phrase('count', {count: state.progress});
    bar?.setAttribute('aria-valuenow', String(state.progress));
    bar?.setAttribute('aria-valuetext', count + '. ' + getMessage(state, unknown));
    for (const step of this.querySelectorAll('[data-tincture-step]')) step.classList.toggle('is-filled', Number(step.dataset.tinctureStep) <= state.progress);
    this.querySelector('[data-tincture-count]').textContent = count;
    const status = this.querySelector('[data-tincture-status]');
    status.replaceChildren();
    if (state.confirmed && !unknown) {
      const heading = document.createElement('strong');
      heading.textContent = phrase('unlocked');
      status.append(heading, document.createTextNode(' '));
    }
    status.append(document.createTextNode(getMessage(state, unknown)));
    const savings = this.querySelector('[data-tincture-savings]');
    savings.hidden = !state.savings || unknown;
    savings.textContent = phrase('savings', {amount: current?.savingsFormatted || ''});
    const next = this.querySelector('[data-tincture-next]');
    next.hidden = !state.confirmed || (!state.nextProgress && !state.missingReward) || unknown;
    next.textContent = state.missingReward ? phrase('next_not_applied') : phrase('next', {count: state.nextProgress, remaining: state.nextRemaining});
    const cta = this.querySelector('[data-tincture-action]');
    const ctaKey = state.confirmed ? 'view_cart' : state.quantity >= 5 ? 'review' : state.quantity === 4 ? 'choose_fifth' : state.quantity > 0 ? 'choose_more' : 'shop';
    cta.firstChild.textContent = phrase(ctaKey);
    cta.href = state.confirmed ? Theme.routes.cart_url : config.collectionUrl + '#ResultsList';
    cta.hidden = cart && state.confirmed;
    this.querySelector('[data-tincture-retry]').hidden = !unknown;
    const recommendations = this.querySelector('[data-tincture-recommendations]');
    if (recommendations) recommendations.hidden = !state.recommend || unknown;
    const recHeading = this.querySelector('[data-tincture-rec-heading]');
    if (recHeading) recHeading.textContent = phrase(state.quantity === 4 ? 'choose_fifth' : state.confirmed ? 'complete_next' : 'complete_offer');
    for (const button of this.querySelectorAll('[data-tincture-recommendation] button[type="submit"]')) button.disabled = inFlight.size > 0;
  }
}

if (runtime && !customElements.get('tincture-offer')) {
  customElements.define('tincture-offer', TinctureOffer);
  document.addEventListener(StandardEvents.cartLinesUpdate, handleCartEvent);
  document.addEventListener(StandardEvents.cartDiscountUpdate, handleCartEvent);
  document.addEventListener(ThemeEvents.cartSectionRestored, () => { if (!inFlight.size) { const snapshot = readSnapshot(document.querySelector('cart-items-component') || document); if (snapshot) { current = snapshot; update(false); } } });
  window.addEventListener('pageshow', event => {
    const navigation = performance.getEntriesByType('navigation')[0];
    if (event.persisted || navigation?.type === 'back_forward') refresh();
  });
  document.addEventListener('visibilitychange', () => {
    if (document.hidden) hiddenAt = Date.now();
    else if (hiddenAt) { hiddenAt = 0; refresh(); }
  });
  document.addEventListener('shopify:section:load', () => {
    const snapshot = readSnapshot(document.querySelector('cart-items-component') || document);
    if (snapshot && !inFlight.size) { current = snapshot; update(false); }
  });
}
